/**
 * Phase 10D: Premium history line chart with Chart.js.
 * Fallback to PremiumHistoryTable if Chart unavailable or data < 2 points.
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from 'chart.js';
import { formatCurrency, formatRenewalDate } from '../../utils/renewalHelpers';
import { generateChartData } from '../../utils/renewalHelpers';
import t from '../../utils/renewalsTranslations';
import PremiumHistoryTable from './PremiumHistoryTable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler);

function formatCurrencyCompact(amount, locale = 'en') {
  if (amount == null) return '—';
  const sym = locale === 'ar' ? 'د.أ' : '$';
  if (amount >= 1000) return `${sym}${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
  return formatCurrency(amount, 'USD', locale);
}

export default function PremiumHistoryChart({ premiumHistory = [], locale = 'en', maxMonths = 24 }) {
  const safeData = Array.isArray(premiumHistory) ? premiumHistory : [];
  const chartData = useMemo(() => {
    const out = generateChartData(safeData, locale, maxMonths);
    if (out?.datasets?.[0]) {
      out.datasets[0].borderWidth = 2;
      out.datasets[0].pointRadius = 5;
      out.datasets[0].backgroundColor = 'rgba(25, 118, 210, 0.2)';
    }
    return out;
  }, [safeData, locale, maxMonths]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items?.[0]?.dataIndex;
              const row = safeData[idx];
              return row ? formatRenewalDate(row.effective_date, locale) : '';
            },
            label: (context) => {
              const idx = context.dataIndex;
              const row = safeData[idx];
              const lines = [`Premium: ${formatCurrency(row?.premium_amount, 'USD', locale)}`];
              if (row?.change_amount != null) lines.push(`Change: ${formatCurrency(row.change_amount, 'USD', locale)}${row?.change_percentage != null ? ` (${row.change_percentage}%)` : ''}`);
              if (row?.change_reason) lines.push(`Reason: ${t(`renewals.changeReasons.${row.change_reason}`) || row.change_reason}`);
              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxRotation: 0, autoSkip: true },
        },
        y: {
          grid: { color: '#f0f0f0' },
          ticks: {
            callback: (value) => (typeof value === 'number' ? formatCurrencyCompact(value, locale) : value),
          },
        },
      },
    }),
    [safeData, locale]
  );

  const hasChart = typeof ChartJS !== 'undefined';
  if (!hasChart || safeData.length < 2) {
    if (safeData.length >= 2) return <PremiumHistoryTable data={safeData} />;
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary" variant="body2">
          {t('renewals.history.notEnoughData') || 'Not Enough Data Yet'}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          {t('renewals.history.notEnoughDataDesc') || 'Premium history will appear after your first renewal'}
        </Typography>
        {safeData.length === 1 && <PremiumHistoryTable data={safeData} />}
      </Box>
    );
  }

  try {
    return (
      <Box sx={{ height: '100%', minHeight: 280 }}>
        <Line
          data={chartData}
          options={options}
          datasetIdKey="premium"
        />
      </Box>
    );
  } catch (err) {
    return <PremiumHistoryTable data={safeData} />;
  }
}
